import { ChangeEvent } from "react";
import { X, ImageIcon } from "lucide-react";
import { DropdownOption } from "../../types/dropdown";

interface AddRequestModalProps {
  showAddModal: boolean;
  closeAddModal: () => void;
  addModalMall: number | null;
  setAddModalMall: (mall: number | null) => void;
  addModalTenant: number | null;
  setAddModalTenant: (tenant: number | null) => void;
  addModalNotes: string;
  setAddModalNotes: (notes: string) => void;
  addModalImages: File[];
  setAddModalImages: (images: File[]) => void;
  modalMallOptions: DropdownOption[];
  modalTenantOptions: DropdownOption[];
  isSubmitting: boolean;
  handleSubmitAddRequest: () => Promise<void>;
}

const AddRequestModal = ({
  showAddModal,
  closeAddModal,
  addModalMall,
  setAddModalMall,
  addModalTenant,
  setAddModalTenant,
  addModalNotes,
  setAddModalNotes,
  addModalImages,
  setAddModalImages,
  modalMallOptions,
  modalTenantOptions,
  isSubmitting,
  handleSubmitAddRequest,
}: AddRequestModalProps) => {
  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setAddModalImages([...addModalImages, ...filesArray]);
    }
  };

  const removeImage = (index: number) => {
    setAddModalImages(addModalImages.filter((_, i) => i !== index));
  };

  if (!showAddModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fadeIn">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Add New Request</h2>
          <button onClick={closeAddModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Mall <span className="text-red-500">*</span>
            </label>
            <select
              value={addModalMall ?? ""}
              onChange={(e) => {
                const val = e.target.value ? Number(e.target.value) : null;
                setAddModalMall(val);
                setAddModalTenant(null);
              }}
              disabled={modalMallOptions.length === 1}
              className={`w-full px-3 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-blue-500 ${
                modalMallOptions.length === 1
                  ? "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed dark:bg-gray-800 dark:text-gray-500 dark:border-gray-700"
                  : "bg-white border-gray-200 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-100"
              }`}
            >
              <option value="">Select Mall</option>
              {modalMallOptions.map((mall) => (
                <option key={mall.value} value={mall.value}>
                  {mall.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tenant <span className="text-red-500">*</span>
            </label>
            <select
              value={addModalTenant ?? ""}
              onChange={(e) => setAddModalTenant(e.target.value ? Number(e.target.value) : null)}
              disabled={!addModalMall || modalTenantOptions.length === 1}
              className={`w-full px-3 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-blue-500 ${
                !addModalMall || modalTenantOptions.length === 1
                  ? "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed dark:bg-gray-800 dark:text-gray-500 dark:border-gray-700"
                  : "bg-white border-gray-200 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-100"
              }`}
            >
              <option value="">Select Tenant</option>
              {modalTenantOptions.map((tenant) => (
                <option key={tenant.value} value={tenant.value}>
                  {tenant.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Request Notes</label>
            <textarea
              value={addModalNotes}
              onChange={(e) => setAddModalNotes(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-100"
              placeholder="Enter request notes..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Upload Images</label>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-blue-400 dark:hover:border-blue-500 transition-colors">
              <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" id="image-upload" />
              <label htmlFor="image-upload" className="cursor-pointer flex flex-col items-center">
                <ImageIcon className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-2" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Click to upload images</span>
                <span className="text-xs text-gray-500 dark:text-gray-500 mt-1">PNG, JPG up to 10MB</span>
              </label>
            </div>

            {addModalImages.length > 0 && (
              <div className="mt-4 grid grid-cols-3 gap-3">
                {addModalImages.map((file, index) => (
                  <div key={index} className="relative group">
                    <img src={URL.createObjectURL(file)} alt={`Preview ${index}`} className="w-full h-24 object-cover rounded-lg" />
                    <button onClick={() => removeImage(index)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="w-4 h-4" />
                    </button>
                    <div className="mt-1 text-xs text-gray-600 dark:text-gray-400 truncate">{file.name}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-6 flex justify-end gap-3 z-10">
          <button
            onClick={closeAddModal}
            disabled={isSubmitting}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmitAddRequest}
            disabled={isSubmitting}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? "Submitting..." : "Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddRequestModal;